import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CODE, RESPONSE_MESSAGE } from 'src/app/modules/utils/Constant';
import SaveTemplateModal from './SaveTemplateModal';
import TemplateListModal from './TemplateListModal';
import { ActionType, ServiceTemplateButtonProps, ServiceTemplateRequest } from './types';
import { getTemplate, saveTemplate, getTemplates, getRecordHistories, getRecordHistory } from './services';
import { TREATMENT_HISTORY_COLUMNS } from './constants';
import { useBenhAnContext } from 'src/app/modules/phan-he-noi-tru-dieu-tri/components/benh-an/context/BenhAnContext';

const TreatmentTemplateButton = ({
	onSelectMau,
	label = 'Mẫu phiếu',
	drop = 'down',
	variant = 'fill',
	tableTitle,
	getRowData,
	columns,
	onSaveValidationFn,
	onSelectValidationFn,
	onConvertData,
	getSaveParams,
	onSaveSuccess,
	renderAddModalComponent,
	multiSelect,
	disabled,
	searchParams = {},
}: ServiceTemplateButtonProps) => {
	const { phieuBenhAn } = useBenhAnContext();
	const [action, setAction] = useState<{
		open: boolean;
		type: ActionType | null;
	}>({
		open: false,
		type: null,
	});

	const onTemplateSelect = async (id: string | string[]) => {
		if (onSelectValidationFn && !onSelectValidationFn()) {
			return;
		}

		try {
			if (Array.isArray(id)) {
				const result = await Promise.all(id.map((id: string) => getTemplate(id)));
				const ordersSet = result.map(({ data }) => data);
				onSelectMau({
					...ordersSet[0],
				});
				onCloseModal();
				toast.success('Sử dụng mẫu thành công');
			} else {
				const { code, data } = await getTemplate(id);
				
				if (CODE.SUCCESS === code) {
					onSelectMau(data);
					onCloseModal();
					toast.success('Sử dụng mẫu thành công');
				}
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const onRecordHistorySelect = async (id: string | string[]) => {

		try {
			const { code, data } = await getRecordHistory({
				id,
				type: phieuBenhAn?.attributes?.templateType,
				typeName: phieuBenhAn?.name
			});

			if (CODE.SUCCESS === code) {
				let dataConverted = JSON.stringify(onConvertData?.(data) || data);
				onSelectMau(dataConverted);
				onCloseModal();
				toast.success('Sử dụng mẫu thành công');
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	const onCloseModal = () => {
		setAction({
			open: false,
			type: null,
		});
	};

	const onSaveTemplate = () => {
		if (onSaveValidationFn && !onSaveValidationFn()) {
			return;
		}

		setAction({
			open: true,
			type: 'save',
		});
	};

	const onViewTemplate = () => {
		setAction({
			open: true,
			type: 'viewList',
		});
	};

	const onViewHistory = () => {
		setAction({
			open: true,
			type: 'viewHistory',
		});
	};

	const onSave = async (thongTinMau: any) => {
		try {
			const request: ServiceTemplateRequest = {
				...thongTinMau,
				...getSaveParams(),
			};
			const { code } = await saveTemplate(request);

			if (CODE.SUCCESS === code) {
				toast.success('Lưu mẫu thành công');
				onSaveSuccess && onSaveSuccess();
				onCloseModal();
			}
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		}
	};

	return (
		<Dropdown
			className="dropdown-btn menu-icon"
			drop={drop}
			hidden={!phieuBenhAn?.attributes?.isSavingTemplateActivated && !phieuBenhAn?.attributes?.isUsingTemplateActivated}
		>
			<Dropdown.Toggle className={`btn-${variant}`} disabled={disabled}>
				{label}
				<i className="bi bi-caret-down-fill spaces pl-4 pr-0 mr-0 pt-2"></i>
			</Dropdown.Toggle>

			<Dropdown.Menu
				className={`${drop === 'up' ? ' dropdown-actions-up' : 'dropdown-actions'}`}
			>
				<Dropdown.Item onClick={onSaveTemplate} hidden={!phieuBenhAn?.attributes?.isSavingTemplateActivated as boolean}>
					<div className="ps-5 spaces line-height-30">Lưu vào mẫu mới</div>
				</Dropdown.Item>
				<Dropdown.Item onClick={onViewTemplate} hidden={!phieuBenhAn?.attributes?.isSavingTemplateActivated as boolean}>
					<div className="ps-5 spaces line-height-30">Danh sách các mẫu đã tạo</div>
				</Dropdown.Item>
				<Dropdown.Item onClick={onViewHistory} hidden={!phieuBenhAn?.attributes?.isUsingTemplateActivated as boolean}>
					<div className="ps-5 spaces line-height-30">Danh sách phiếu cũ</div>
				</Dropdown.Item>
			</Dropdown.Menu>

			{action.open && action.type === 'save' && (
				<SaveTemplateModal
					open={action.open}
					handleClose={onCloseModal}
					onSave={(value: any) => {
						onSave(value);
					}}
				/>
			)}

			{action.open && action.type === 'viewList' && (
				<TemplateListModal
					open={action.open}
					handleClose={onCloseModal}
					onSelect={(value: any) => {
						onTemplateSelect(value);
					}}
					tableTitle={tableTitle}
					getRowData={getRowData}
					columns={columns}
					renderAddModal={renderAddModalComponent}
					multiSelect={multiSelect}
					searchFunc={getTemplates}
					searchParams={searchParams}
				/>
			)}

			{action.open && action.type === 'viewHistory' && (
				<TemplateListModal
					tableTitle="Danh sách phiếu cũ"
					open={action.open}
					handleClose={onCloseModal}
					onSelect={(value: any) => {
						onRecordHistorySelect(value);
					}}
					getRowData={getRowData}
					columns={TREATMENT_HISTORY_COLUMNS}
					renderAddModal={renderAddModalComponent}
					multiSelect={multiSelect}
					searchFunc={getRecordHistories}
					searchParams={searchParams}
					isViewHistory
				/>
			)}
		</Dropdown>
	);
};

export default TreatmentTemplateButton;
